import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getInvoiceDetail, markInvoicePaid, markInvoiceSent } from "@/features/ops/api/opsApi";
import { useOpsStore } from "@/features/ops/model/opsStore";
import { ROLE_LABELS } from "@/features/shifts/types";
import { Badge, Button, type BadgeProps } from "@/shared/ui";

interface Props {
  invoiceId: string;
  onClose: () => void;
}

function fmtLong(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function fmtShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function InvoiceDetailModal({ invoiceId, onClose }: Props) {
  const token = useOpsStore((s) => s.token)!;
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops", "invoices", invoiceId],
    queryFn: () => getInvoiceDetail(token, invoiceId),
  });

  const sent = useMutation({
    mutationFn: () => markInvoiceSent(token, invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops", "invoices"] });
      // The venue reads its own invoices under a separate key — flush it so
      // the venue sees the SENT status without a manual refresh.
      qc.invalidateQueries({ queryKey: ["invoices", "me"] });
    },
  });
  const paid = useMutation({
    mutationFn: () => markInvoicePaid(token, invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops", "invoices"] });
      qc.invalidateQueries({ queryKey: ["invoices", "me"] });
    },
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fee = data
    ? (parseFloat(data.total_charge) - parseFloat(data.total_worker_pay)).toFixed(2)
    : "0.00";
  const shortId = invoiceId.slice(0, 8).toUpperCase();

  return (
    <div
      className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-overlay/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl animate-scale-in overflow-y-auto rounded-xl border border-border bg-surface-raised p-6 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading && <div className="text-sm text-copy-muted">Loading invoice…</div>}
        {error && <div className="text-sm text-destructive">Could not load invoice.</div>}

        {data && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between border-b border-border pb-4">
              <div>
                <h1 className="text-2xl font-semibold tracking-display-tight text-copy">
                  Invoice
                </h1>
                <p className="mt-1 text-xs text-copy-subtle">
                  #INV-{shortId}
                  <span className="mx-2">·</span>
                  Generated {fmtLong(data.created_at)}
                </p>
              </div>
              <StatusBadge status={data.status} sentAt={data.sent_at} paidAt={data.paid_at} />
            </div>

            {/* From / To */}
            <div className="mt-4 grid grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider text-copy-subtle">From</p>
                <p className="mt-1 font-semibold text-copy">OneShift</p>
                <p className="text-copy-muted">billing@oneshift.ca</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-copy-subtle">Bill to</p>
                <p className="mt-1 font-semibold text-copy">{data.venue_name}</p>
                <p className="text-copy-muted">{data.venue_address}</p>
              </div>
            </div>

            {/* Period */}
            <div className="mt-4 rounded-md bg-surface-sunken px-3 py-2 text-sm">
              <span className="text-copy-muted">Billing period: </span>
              <span className="font-medium text-copy">
                {fmtLong(data.period_start)} – {fmtLong(data.period_end)}
              </span>
            </div>

            {/* Line items */}
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-copy-subtle">
                Line items
              </p>
              {data.line_items.length === 0 ? (
                <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-copy-muted">
                  No shift line items. This invoice may have been generated in error.
                </p>
              ) : (
                <div className="overflow-hidden rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-surface-sunken text-left text-xs uppercase tracking-wider text-copy-muted">
                      <tr>
                        <th className="px-3 py-2 font-medium">Date</th>
                        <th className="px-3 py-2 font-medium">Role</th>
                        <th className="px-3 py-2 font-medium">Worker</th>
                        <th className="px-3 py-2 text-right font-medium">Hours</th>
                        <th className="px-3 py-2 text-right font-medium">Rate</th>
                        <th className="px-3 py-2 text-right font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data.line_items.map((l) => (
                        <tr key={l.shift_id}>
                          <td className="px-3 py-2 text-copy">{fmtShort(l.date)}</td>
                          <td className="px-3 py-2 text-copy-muted">{ROLE_LABELS[l.role]}</td>
                          <td className="px-3 py-2 text-copy">{l.worker_name || "—"}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-copy">
                            {l.hours}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-copy">
                            ${l.pay_rate}
                          </td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-copy">
                            ${l.amount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-copy-muted">Worker pay subtotal</span>
                <span className="tabular-nums text-copy">${data.total_worker_pay}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-copy-muted">
                  OneShift fee ({data.markup_percentage}% markup)
                </span>
                <span className="tabular-nums text-copy">${fee}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
                <span className="text-copy">Total due</span>
                <span className="tabular-nums text-copy">${data.total_charge}</span>
              </div>
            </div>

            {/* Payment instructions */}
            <div className="mt-4 rounded-md border border-info bg-info-light p-3 text-xs text-info-foreground">
              <p className="font-semibold">Payment instructions</p>
              <p className="mt-1">
                Net 7 days. Pay via Interac e-Transfer to{" "}
                <span className="font-medium">billing@oneshift.ca</span> with reference{" "}
                <span className="font-medium">INV-{shortId}</span>.
              </p>
            </div>

            {/* Actions */}
            <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <div className="flex gap-2">
                {data.status === "GENERATED" && (
                  <Button onClick={() => sent.mutate()} disabled={sent.isPending}>
                    {sent.isPending ? "Sending…" : "Send to venue"}
                  </Button>
                )}
                {data.status === "SENT" && (
                  <Button onClick={() => paid.mutate()} disabled={paid.isPending}>
                    {paid.isPending ? "Marking…" : "Mark paid"}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatusBadge({
  status,
  sentAt,
  paidAt,
}: {
  status: string;
  sentAt: string | null;
  paidAt: string | null;
}) {
  const variants: Record<string, BadgeProps["variant"]> = {
    GENERATED: "warning",
    SENT: "info",
    PAID: "success",
  };
  const labels: Record<string, string> = {
    GENERATED: "Draft",
    SENT: "Sent",
    PAID: "Paid",
  };
  const detail =
    status === "PAID" && paidAt
      ? `Paid ${fmtShort(paidAt)}`
      : status === "SENT" && sentAt
        ? `Sent ${fmtShort(sentAt)}`
        : null;
  return (
    <div className="text-right">
      <Badge variant={variants[status] ?? "neutral"} dot>
        {labels[status] ?? status}
      </Badge>
      {detail && <p className="mt-1 text-xs text-copy-subtle">{detail}</p>}
    </div>
  );
}
